import { memo } from "react";

import { cn } from "~/core/generic/shadcn-utils";

import Avatar from "../../Avatar";
import { AvatarProps } from "../../Avatar/interfaces";
import Icon from "../../Icon";
import { ICON_COLOR_MAP } from "../constants";
import { ButtonVariant } from "../enums";
import type { ButtonProps } from "../interfaces";

import { ButtonLoading } from "./ButtonLoading";
import { Shortcut } from "./Shortcut";

interface Props {
  variant: ButtonVariant;
  loading?: ButtonProps["loading"];
  avatar?: ButtonProps["avatar"];
  icon12pxLeft?: ButtonProps["icon12pxLeft"];
  icon18pxLeft?: ButtonProps["icon18pxLeft"];
  label?: ButtonProps["label"];
  icon18pxRight?: ButtonProps["icon18pxRight"];
  icon12pxRight?: ButtonProps["icon12pxRight"];
  shortcutKey?: ButtonProps["shortcutKey"];
  disabled?: ButtonProps["disabled"];
}

/**
 * ButtonContent component handles the rendering of button interior elements
 * including icons, text, avatars, and loading states.
 */
export const ButtonContent = memo(
  ({
    variant,
    loading = false,
    avatar,
    icon12pxLeft,
    icon18pxLeft,
    label,
    icon18pxRight,
    icon12pxRight,
    shortcutKey,
    disabled,
  }: Props) => {
    return (
      <div className="relative flex items-center justify-center min-w-full">
        {/* Main content */}
        <div
          className={cn(
            "flex items-center justify-center octolane-button-content",
            { invisible: loading },
          )}
        >
          {/* Avatar if present */}
          {avatar && (
            <Avatar
              {...(avatar as AvatarProps)}
              size={18}
              status={disabled ? "disabled" : undefined}
            />
          )}

          {/* Left icons */}
          {icon12pxLeft && (
            <Icon
              name={icon12pxLeft}
              color={
                disabled
                  ? ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .disabled
                  : ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .default
              }
            />
          )}
          {icon18pxLeft && (
            <Icon
              name={icon18pxLeft}
              color={
                disabled
                  ? ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .disabled
                  : ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .default
              }
            />
          )}

          {/* Button label */}
          {label && (
            <span className="octolane-button-text text-nowrap">{label}</span>
          )}

          {/* Right icons */}
          {icon18pxRight && (
            <Icon
              name={icon18pxRight}
              color={
                disabled
                  ? ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .disabled
                  : ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .default
              }
            />
          )}
          {icon12pxRight && (
            <Icon
              name={icon12pxRight}
              color={
                disabled
                  ? ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .disabled
                  : ICON_COLOR_MAP[variant as keyof typeof ICON_COLOR_MAP]
                      .default
              }
            />
          )}

          {/* Shortcut key indicator */}
          {shortcutKey && (
            <Shortcut
              variant={shortcutKey.variant}
              keys={shortcutKey.keys}
              buttonVariant={variant}
              disabled={disabled}
            />
          )}
        </div>

        {/* Loading indicator */}
        {loading && <ButtonLoading />}
      </div>
    );
  },
);

ButtonContent.displayName = "ButtonContent";
